import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { stationHosts, type Host } from "@/mocks/adminHosts";
import { programs, type Program } from "@/mocks/programs";
import { clips, duracionLabels as clipDuracionLabels, formatearNumero, type Clip } from "@/mocks/clips";
import TvCarousel from "@/pages/tv-app/components/TvCarousel";

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

export default function TvHostDetailPage() {
  const navigate = useNavigate();
  const { hostId } = useParams<{ hostId: string }>();
  const [host, setHost] = useState<Host | null>(null);
  const [hostPrograms, setHostPrograms] = useState<Program[]>([]);
  const [relatedClips, setRelatedClips] = useState<Clip[]>([]);
  const [focusedBtn, setFocusedBtn] = useState<string>("back");
  const [showQr, setShowQr] = useState(false);

  const programsRef = useRef<HTMLDivElement>(null);

  const buttons = ["back", "programs", "qr"] as const;

  useEffect(() => {
    if (!hostId) return;
    const numId = Number(hostId);
    if (isNaN(numId)) return;
    const h = stationHosts.find((h) => h.id === numId);
    if (!h) return;
    setHost(h);
    const progs = programs.filter((p) => p.hosts.some((ph) => ph.name === h.name)).slice(0, 6);
    setHostPrograms(progs);
    setRelatedClips(clips.filter((c) => c.locutorNombre === h.name).slice(0, 6));
  }, [hostId]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        const idx = buttons.indexOf(focusedBtn as typeof buttons[number]);
        setFocusedBtn(buttons[(idx + 1) % buttons.length]);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const idx = buttons.indexOf(focusedBtn as typeof buttons[number]);
        setFocusedBtn(buttons[(idx - 1 + buttons.length) % buttons.length]);
      } else if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault();
        if (showQr) { setShowQr(false); return; }
        navigate("/tv-app");
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (focusedBtn === "back") navigate("/tv-app");
        else if (focusedBtn === "programs") programsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        else if (focusedBtn === "qr") setShowQr(true);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [focusedBtn, navigate, showQr]);

  if (!host) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center select-none">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-white/5 flex items-center justify-center mb-6"><i className="ri-user-voice-line text-white/40 text-3xl"></i></div>
          <h1 className="text-2xl font-bold text-white mb-3">Locutor no encontrado</h1>
          <button onClick={() => navigate("/tv-app")} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold cursor-pointer">Volver</button>
        </div>
      </div>
    );
  }

  const statusColor = host.status === "on_air" ? "bg-red-500/90" : host.status === "active" ? "bg-emerald-500/90" : "bg-foreground-400/90";
  const statusLabel = host.status === "on_air" ? "AL AIRE" : host.status === "active" ? "ACTIVO" : "INACTIVO";

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white select-none overflow-x-hidden">
      <div className="relative h-[300px] lg:h-[400px] overflow-hidden">
        <img src={host.photoUrl} alt="" className="w-full h-full object-cover opacity-30 blur-md scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/40 to-[#0a0a0f]/20"></div>
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 lg:px-10 py-5 z-10">
          <button onClick={() => navigate("/tv-app")} className={`flex items-center gap-2 px-5 py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-semibold ${focusedBtn === "back" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30" : "bg-white/10 hover:bg-white/15 text-white"}`}>
            <i className="ri-arrow-left-line text-lg"></i>Volver
          </button>
          <span className={`px-3 py-1.5 rounded-full ${statusColor} text-white text-xs font-bold flex items-center gap-1.5`}>
            {host.status === "on_air" && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>}{statusLabel}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 z-10 px-6 lg:px-10 pb-8 flex items-end gap-6">
          <div className="w-28 h-28 lg:w-36 lg:h-36 rounded-3xl overflow-hidden ring-4 ring-white/10 bg-[#0a0a0f] flex-shrink-0">
            <img src={host.photoUrl} alt={host.name} className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-white">{host.name}</h1>
            <p className="text-lg text-white/50 mt-1">@{host.username}</p>
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-10 py-8 space-y-8">
        <div className="flex items-center gap-10 flex-wrap">
          <div><p className="text-3xl font-bold text-white">{formatCount(host.followers)}</p><p className="text-sm text-white/50 mt-1">Seguidores</p></div>
          <div className="w-px h-10 bg-white/10"></div>
          <div><p className="text-3xl font-bold text-accent-400">{formatCount(host.totalInteractions)}</p><p className="text-sm text-white/50 mt-1">Interacciones</p></div>
        </div>

        <p className="text-base text-white/60 leading-relaxed max-w-3xl">{host.bio}</p>

        {hostPrograms.length > 0 && (
          <section ref={programsRef}>
            <h2 className="text-2xl font-bold text-white mb-5 flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center"><i className="ri-mic-line text-emerald-400 text-sm"></i></div>Programas ({hostPrograms.length})</h2>
            <TvCarousel>
              {hostPrograms.map((prog) => (
                <div key={prog.id} tabIndex={0} onClick={() => navigate(`/tv-app/program/${prog.slug}`)} data-tv-focusable="true" className="relative flex-shrink-0 w-[300px] aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-emerald-400 focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-emerald-400/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f]">
                  <img src={prog.coverUrl} alt={prog.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-5"><h3 className="text-lg font-bold text-white truncate">{prog.name}</h3><p className="text-sm text-white/70 mt-1">{prog.radioName}</p><p className="text-xs text-white/50 mt-1">{prog.schedule}</p></div>
                </div>
              ))}
            </TvCarousel>
          </section>
        )}

        {relatedClips.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold text-white mb-5 flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-accent-400/10 flex items-center justify-center"><i className="ri-film-line text-accent-400 text-sm"></i></div>Clips destacados ({relatedClips.length})</h2>
            <TvCarousel>
              {relatedClips.map((clip) => (
                <div key={clip.id} tabIndex={0} onClick={() => navigate(`/tv-app/video/${clip.id}`)} data-tv-focusable="true" className="relative flex-shrink-0 w-[340px] aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-accent-400 focus:ring-offset-4 focus:ring-offset-[#0a0a0f]">
                  <img src={clip.imagenUrl} alt={clip.titulo} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-5"><h3 className="text-lg font-bold text-white truncate">{clip.titulo}</h3><p className="text-sm text-white/60 mt-1">{formatearNumero(clip.vistas)} vistas</p></div>
                </div>
              ))}
            </TvCarousel>
          </section>
        )}

        <div className="flex items-center justify-center gap-3 pt-4 pb-8">
          <button onClick={() => navigate("/tv-app")} className={`flex items-center gap-2 px-5 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-bold ${focusedBtn === "back" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"}`}><i className="ri-arrow-left-line text-lg"></i>Volver</button>
          <button onClick={() => setShowQr(true)} className={`flex items-center gap-2 px-5 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-bold ${focusedBtn === "qr" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"}`}><i className="ri-qr-code-line text-lg"></i>Abrir en el celu</button>
        </div>

        {showQr && (
          <div className="fixed inset-0 z-50 bg-[#0a0a0f]/90 backdrop-blur-sm flex items-center justify-center" onClick={() => setShowQr(false)}>
            <div className="bg-[#0f0f15] border border-white/10 rounded-3xl p-8 max-w-md w-full mx-4 text-center" onClick={(e) => e.stopPropagation()}>
              <div className="w-48 h-48 mx-auto rounded-2xl bg-white p-3 mb-6"><img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`https://radiofrecuenciaweb.com/host/${host.username}`)}`} alt="QR" className="w-full h-full object-contain rounded-xl" /></div>
              <h3 className="text-xl font-bold text-white mb-2">Seguir en el celular</h3>
              <p className="text-sm text-white/50 mb-2">Escaneá para seguir a {host.name} desde tu celular.</p>
              <p className="text-sm text-white/30 font-mono mb-6">radiofrecuenciaweb.com/host/{host.username}</p>
              <button onClick={() => setShowQr(false)} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold cursor-pointer">Cerrar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}